/** The delete confirm for a schedule, shared by every list that offers it.
 * Owns the request; the caller learns of success through `onDeleted`. */
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../shared/ui/alert-dialog';
import { botTaskSchedulesService, type BotSchedule } from '../../../services/BotTaskSchedulesService';

interface ScheduleDeleteDialogProps {
  /** The schedule being deleted; null keeps the dialog closed. */
  schedule: BotSchedule | null;
  /** Names whose run it is when it is not the viewer's own. */
  ownerName?: string | null;
  onClose: () => void;
  onDeleted: (schedule: BotSchedule) => void;
}

export function ScheduleDeleteDialog({ schedule, ownerName, onClose, onDeleted }: ScheduleDeleteDialogProps) {
  const confirm = async () => {
    if (!schedule) return;
    onClose();
    try { await botTaskSchedulesService.remove(schedule.id); onDeleted(schedule); toast.success('Schedule deleted'); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'The schedule could not be deleted.'); }
  };
  return (
    <AlertDialog open={schedule !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this schedule?</AlertDialogTitle>
          <AlertDialogDescription>
            {ownerName === undefined ? '' : `${ownerName ?? 'Its owner'} will no longer get this run. `}{schedule?.description}.
            {ownerName === undefined ? ' It stops running. Tasks it already started stay in the list.' : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => void confirm()} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
